import React from 'react';
import { Home, ListChecks, Calculator, Menu, X, UserPlus, Briefcase, Tag, CalendarHeart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion'; 

interface NavLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  isHighlighted?: boolean;
  hasNewBadge?: boolean;
  badgeColor?: 'red' | 'amber';
  onClick?: () => void;
}

function NavLink({ to, icon, label, isActive, isHighlighted, hasNewBadge, badgeColor = 'red', onClick }: NavLinkProps) {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onClick) onClick();
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`flex flex-col items-center p-2 pt-1.5 transition-all duration-200 active:scale-95 ${
        isActive
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
      } relative group`}
      aria-label={label}
      title={label}
    >
      {isActive && (
        <motion.div
          layoutId="mobile-nav-indicator"
          className="absolute inset-x-0 -top-0.5 h-0.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-full"
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}
      {isHighlighted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: [0.7, 1, 0.7],
            scale: [1, 1.15, 1],
            boxShadow: [
              '0 0 0 0 rgba(239, 65, 54, 0.4)',
              '0 0 0 10px rgba(239, 65, 54, 0)',
              '0 0 0 0 rgba(239, 65, 54, 0.4)'
            ]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "loop"
          }}
          className="absolute inset-[-4px] rounded-full bg-primary-100 dark:bg-primary-900/20 z-[-1]"
        />
      )}
      <div className={`relative w-7 h-7 flex items-center justify-center mb-0.5 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>
        {React.cloneElement(icon as React.ReactElement, {
          className: "w-5 h-5"
        })}
        {hasNewBadge && !isActive && (
          <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white dark:ring-gray-900 animate-pulse ${
            badgeColor === 'amber' ? 'bg-amber-500' : 'bg-red-500'
          }`} />
        )}
      </div>
      <span className={`text-xs font-semibold transition-all duration-200 ${isActive ? 'scale-105' : ''}`}>{label}</span>
    </Link>
  );
}

interface MobileNavBarProps {
  onOpenMenu: () => void;
  isMenuOpen: boolean;
  hasNewMatching?: boolean;
  hasNewActivities?: boolean;
}

export function MobileNavBar({ onOpenMenu, isMenuOpen, hasNewMatching, hasNewActivities }: MobileNavBarProps) {
  const location = useLocation();

  // Determine if we're on an ultra-narrow screen
  const [isNarrow, setIsNarrow] = React.useState(false);

  // Update state on window resize
  React.useEffect(() => {
    const checkScreenWidth = () => {
      setIsNarrow(window.innerWidth < 350);
    };
    
    // Initial check
    checkScreenWidth();
    
    // Listen for resize events
    window.addEventListener('resize', checkScreenWidth);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenWidth);
  }, []);
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 10px)' }}>
      <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-t border-gray-200/30 dark:border-gray-700/30 shadow-2xl shadow-black/5">
        <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/5 dark:to-white/[0.02] pointer-events-none"></div>
        <div className="relative grid grid-cols-5 gap-0 px-2 py-2">
          <NavLink
            to="/"
            icon={<Home className="w-6 h-6" />}
            label="Accueil"
            isActive={location.pathname === '/'}
          />

          <NavLink
            to="/simulateur-budget"
            icon={<Calculator className="w-6 h-6" />}
            label="Budget"
            isActive={location.pathname === '/simulateur-budget'}
          />

          <NavLink
            to="/matching"
            icon={<UserPlus className="w-6 h-6" />}
            label="PVTmates"
            isActive={location.pathname === '/matching'}
            hasNewBadge={hasNewMatching}
          />

          <NavLink
            to="/activites"
            icon={<CalendarHeart className="w-6 h-6" />}
            label="Activités"
            isActive={location.pathname === '/activites'}
            hasNewBadge={hasNewActivities}
            badgeColor="amber"
          />

          <button
            onClick={onOpenMenu}
            className="flex flex-col items-center p-2 pt-1.5 text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all duration-200 active:scale-95 relative group"
            aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            title={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            <div className="w-7 h-7 flex items-center justify-center mb-0.5 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/0 to-cyan-400/0 group-hover:from-emerald-400/20 group-hover:to-cyan-400/20 rounded-lg transition-all duration-300" />
              <motion.div
                animate={isMenuOpen ? "open" : "closed"}
                className="relative w-5 h-5 flex flex-col items-center justify-center"
              >
                <motion.span
                  variants={{
                    closed: { rotate: 0, y: -3 },
                    open: { rotate: 45, y: 0 }
                  }}
                  transition={{ duration: 0.3, type: 'spring', stiffness: 300 }}
                  className="absolute w-5 h-0.5 bg-current rounded-full"
                />
                <motion.span
                  variants={{
                    closed: { opacity: 1, scale: 1 },
                    open: { opacity: 0, scale: 0 }
                  }}
                  transition={{ duration: 0.2 }}
                  className="absolute w-5 h-0.5 bg-current rounded-full"
                />
                <motion.span
                  variants={{
                    closed: { rotate: 0, y: 3 },
                    open: { rotate: -45, y: 0 }
                  }}
                  transition={{ duration: 0.3, type: 'spring', stiffness: 300 }}
                  className="absolute w-5 h-0.5 bg-current rounded-full"
                />
              </motion.div>
            </div>
            <motion.span
              animate={{ scale: isMenuOpen ? 0.9 : 1 }}
              className="text-xs font-semibold"
            >
              {isMenuOpen ? "Fermer" : "Menu"}
            </motion.span>
          </button>
        </div>
        <div className="h-safe-area-inset-bottom"></div>
      </div>
    </div>
  );
}